// askSmart-ui/src/pages/TOSP/components/TenantSalesPanel.tsx
import React, { useState } from "react";
import { useAuth } from "../../../hooks/useAuth";
import { formatCurrency } from "../format";
import { MONTH_NAMES, monthName, SALES_DUE_DAY } from "../portal/portalSummary";
import {
  bestYearForMonth,
  monthOnMonth,
  monthsOwing,
  reportedYears,
  useStoredSales,
  yearOnYear,
} from "../portal/tenantSales";
import { parseNumeric } from "../validation";

interface TenantSalesPanelProps {
  /** Empty while a tenant is being added — nothing can have been reported yet. */
  tenantId: string;
  /** Viewing rather than editing: the same sheet, nothing changeable. */
  readOnly?: boolean;
  /**
   * The day they began trading, so months before it are not chased.
   *
   * Passed in rather than read here: the panel is given a tenant id, and the
   * record it belongs to is the caller's.
   */
  openedOn?: string | null;
  /** The day the tenancy ended, so months after it are not chased either. */
  closedOn?: string | null;
  /** False where every lease says this tenant files nothing. */
  reportsSales?: boolean;
}

// What the tenant has reported, as the property manager needs to see it: every
// year they've reported, side by side, with what's still outstanding.
//
// The current year's figures are editable here and nowhere else. A submitted
// month is final on the tenant's side — the portal says so before they confirm
// — so this is the only route to a correction, and it keeps what the tenant
// originally reported alongside the corrected figure.
export default function TenantSalesPanel({
  tenantId,
  readOnly = false,
  openedOn,
  closedOn,
  reportsSales,
}: TenantSalesPanelProps) {
  const { sales, amend } = useStoredSales();
  const { user } = useAuth();
  const [editing, setEditing] = useState<{ year: number; month: number } | null>(null);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);

  const thisYear = new Date().getFullYear();
  const years = reportedYears(sales, tenantId);
  const owing = tenantId
    ? monthsOwing(sales, tenantId, new Date(), SALES_DUE_DAY, { openedOn, closedOn, reportsSales })
    : [];
  const best = bestYearForMonth(sales, tenantId);

  if (!tenantId) {
    return (
      <p className="tosp-field-hint">
        Sales are reported by the tenant through their portal. Nothing can be reported
        until this tenant is saved.
      </p>
    );
  }

  if (years.length === 0) {
    return <p className="tosp-field-hint">This tenant hasn't reported any sales yet.</p>;
  }

  const figureFor = (year: number, month: number) =>
    sales.find(
      (entry) =>
        entry.tenantId === tenantId && entry.year === year && entry.month === month,
    );

  const yearTotal = (year: number) =>
    sales
      .filter((entry) => entry.tenantId === tenantId && entry.year === year)
      .reduce((sum, entry) => sum + entry.grossSales, 0);

  const startEdit = (year: number, month: number, current: number) => {
    setEditing({ year, month });
    setDraft(String(current));
    setError(null);
  };

  const commit = () => {
    if (!editing) return;
    const value = parseNumeric(draft);
    if (value === null || value < 0) {
      setError(`${monthName(editing.month)} ${editing.year} must be a number, not negative.`);
      return;
    }
    amend(tenantId, editing, value, user?.email || "");
    setEditing(null);
    setError(null);
  };

  // Fixed widths so a year doesn't reflow the ones beside it; the panel
  // scrolls sideways once they outrun the modal.
  const columns = `92px repeat(${years.length}, 128px)`;

  return (
    <>
      {owing.length > 0 && (
        <div className="tosp-banner tosp-banner-warn">
          <span>
            {owing.length} month{owing.length === 1 ? "" : "s"} outstanding:{" "}
            {owing
              .slice(0, 6)
              .map((month) => `${monthName(month.month)} ${month.year}`)
              .join(", ")}
            {owing.length > 6 && ` and ${owing.length - 6} more`}.
          </span>
        </div>
      )}

      {error && <p className="tosp-field-error">{error}</p>}

      <div className="tosp-sales-scroll">
        <div className="tosp-sales-sheet">
          <div className="tosp-sales-head" style={{ gridTemplateColumns: columns }}>
            <div className="tosp-sales-label" />
            {years.map((year) => (
              <div key={year} className="tosp-sales-year">
                {year}
              </div>
            ))}
          </div>

          {MONTH_NAMES.map((_, index) => {
            const month = index + 1;
            return (
              <div
                key={month}
                className="tosp-sales-row"
                style={{ gridTemplateColumns: columns }}
              >
                <div className="tosp-sales-label">{monthName(month)}</div>
                {years.map((year) => {
                  const entry = figureFor(year, month);
                  if (!entry) {
                    return (
                      <div key={year} className="tosp-sales-cell tosp-sales-missing">
                        —
                      </div>
                    );
                  }

                  const isEditing =
                    editing?.year === year && editing?.month === month;
                  if (isEditing) {
                    return (
                      <div key={year} className="tosp-sales-cell">
                        <input
                          className="manage-input tosp-sales-input"
                          autoFocus
                          inputMode="decimal"
                          aria-label={`${monthName(month)} ${year} Gross Revenue`}
                          value={draft}
                          onChange={(e) => setDraft(e.target.value)}
                          onBlur={commit}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              commit();
                            }
                            if (e.key === "Escape") {
                              setEditing(null);
                              setError(null);
                            }
                          }}
                        />
                      </div>
                    );
                  }

                  const moved = monthOnMonth(sales, tenantId, year, month);
                  const isBest = best.get(month) === year;
                  // Only the current year is correctable. Closed years are
                  // settled — percentage rent has been raised off them.
                  const editable = !readOnly && year === thisYear;

                  return (
                    <div
                      key={year}
                      className={[
                        "tosp-sales-cell",
                        isBest ? "tosp-sales-best" : "",
                        editable ? "tosp-sales-editable" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      title={
                        [
                          isBest ? `Best ${monthName(month)} on record` : "",
                          entry.amendedAt && entry.originalGrossSales !== undefined
                            ? `Corrected from ${formatCurrency(entry.originalGrossSales)}${entry.amendedBy ? ` by ${entry.amendedBy}` : ""}`
                            : "",
                          editable ? "Click to correct" : "",
                        ]
                          .filter(Boolean)
                          .join(" · ") || undefined
                      }
                      onClick={
                        editable ? () => startEdit(year, month, entry.grossSales) : undefined
                      }
                    >
                      <span>{formatCurrency(entry.grossSales)}</span>
                      {/* Always rendered, empty when there's nothing to
                          compare — the figures stay in one column instead of
                          jogging left wherever an arrow appears. */}
                      <span
                        className={`tosp-sales-arrow${
                          moved
                            ? moved.up
                              ? " tosp-sales-arrow-up"
                              : " tosp-sales-arrow-down"
                            : ""
                        }`}
                      >
                        {moved ? (moved.up ? "▲" : "▼") : ""}
                      </span>
                    </div>
                  );
                })}
              </div>
            );
          })}

          <div
            className="tosp-sales-row tosp-sales-total"
            style={{ gridTemplateColumns: columns }}
          >
            <div className="tosp-sales-label">Total</div>
            {years.map((year) => {
              // Like for like, and the same call the tenant's own sheet makes,
              // so the two never disagree: a part year is compared only against
              // the months it actually holds. Seven months of 2026 against a
              // full 2025 would otherwise read as a collapse.
              const change = yearOnYear(sales, tenantId, year);
              const up = (change?.changePercent ?? 0) > 0;
              const moved =
                change && change.changePercent !== null && change.changePercent !== 0;
              const label = !change
                ? undefined
                : change.monthsCompared < 12
                  ? `${Math.abs(change.changePercent ?? 0).toFixed(1)}% ${up ? "up" : "down"} on the same ${change.monthsCompared} months of ${year - 1}`
                  : `${Math.abs(change.changePercent ?? 0).toFixed(1)}% ${up ? "up" : "down"} on ${year - 1}`;

              return (
                <div key={year} className="tosp-sales-cell">
                  <span>{formatCurrency(yearTotal(year))}</span>
                  {/* Always rendered, empty when there is nothing to compare
                      against, so the figures stay aligned down the row. */}
                  <span
                    className={`tosp-sales-arrow${
                      moved ? (up ? " tosp-sales-arrow-up" : " tosp-sales-arrow-down") : ""
                    }`}
                    title={moved ? label : undefined}
                    aria-label={moved ? label : undefined}
                  >
                    {moved ? (up ? "▲" : "▼") : ""}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {!readOnly && (
        <p className="tosp-field-hint">
          {thisYear} figures can be corrected here — click one to change it. Earlier years
          are settled and read-only.
        </p>
      )}
    </>
  );
}
